import React from "react";
import { EmployeeSchedule } from "../../types/employee";
import { getScheduleLocationLabel } from "../../utils/scheduleLocations";

interface MyScheduleProps {
    thisWeeksSchedule: EmployeeSchedule[];
    startDate?: Date;
    endDate?: Date;
    scheduledMinutes: number;
}

type DisplayDay = {
    date: Date;
    dateString: string;
    schedule?: EmployeeSchedule;
};

const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatTime = (date: Date) => date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
});

export const MySchedule: React.FC<MyScheduleProps> = ({
    thisWeeksSchedule,
    startDate,
    endDate,
    scheduledMinutes,
}) => {
    const scheduleByDate = new Map(thisWeeksSchedule.map((schedule) => [schedule.date, schedule]));
    const displayDays: DisplayDay[] = [];

    if (startDate && endDate) {
        const current = new Date(startDate);
        current.setHours(0, 0, 0, 0);
        const finalDate = new Date(endDate);
        finalDate.setHours(23, 59, 59, 999);

        while (current <= finalDate) {
            const date = new Date(current);
            const dateString = formatLocalDate(date);
            displayDays.push({ date, dateString, schedule: scheduleByDate.get(dateString) });
            current.setDate(current.getDate() + 1);
        }
    }

    const totalHours = scheduledMinutes / 60;
    const totalHoursLabel = Number.isInteger(totalHours) ? totalHours.toString() : totalHours.toFixed(2);
    const today = formatLocalDate(new Date());

    return (
        <div className="my-schedule-container">
            <div className="panel-header schedule-panel-header">
                <div>
                    <h2>My Schedule</h2>
                    <p>{displayDays.length} day{displayDays.length === 1 ? "" : "s"} shown</p>
                </div>
                <span className="hours-scheduled">{totalHoursLabel} hrs</span>
            </div>
            <div className="panel-body schedule-panel-body">
                {displayDays.length ? (
                    <ul className="schedule-list">
                        {displayDays.map(({ date, dateString, schedule }) => {
                            const isTimeOff = schedule?.location === "Pto" || schedule?.location === "Time Off";
                            const rowStatus = isTimeOff ? "time-off" : schedule ? "working" : "off";
                            const endIsNextDay = schedule
                                ? formatLocalDate(schedule.shiftStart.toDate()) !== formatLocalDate(schedule.shiftEnd.toDate())
                                : false;

                            return (
                                <li key={dateString} className={`schedule-row ${rowStatus}${dateString === today ? " today" : ""}`}>
                                    <div className="schedule-date-block">
                                        <span className="schedule-weekday">{date.toLocaleDateString("en-US", { weekday: "short" })}</span>
                                        <span className="schedule-calendar-date">{date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                                    </div>
                                    <div className="schedule-shift-block">
                                        {isTimeOff ? (
                                            <><strong>Scheduled time off</strong><span>No shift</span></>
                                        ) : schedule ? (
                                            <>
                                                <strong>{formatTime(schedule.shiftStart.toDate())} – {formatTime(schedule.shiftEnd.toDate())}{endIsNextDay ? " +1 day" : ""}</strong>
                                                <span>{getScheduleLocationLabel(schedule.location)}</span>
                                            </>
                                        ) : (
                                            <><strong>Off</strong><span>No shift scheduled</span></>
                                        )}
                                    </div>
                                    <span className="schedule-status-dot" aria-hidden="true" />
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="schedule-empty">Choose a date range to view your schedule.</p>
                )}
            </div>
        </div>
    );
};

require("./styles.scss");
